import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiModels, setAiModels] = useState({});

  useEffect(() => {
    loadRealData();
    // Load AI models every 30 seconds
    const interval = setInterval(loadAiModels, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAiModels = async () => {
    try {
      const response = await axios.get(`${API}/ai-models/status`);
      console.log("ai models:", response.data)
      setAiModels(response.data);
    } catch (error) {
      console.error('Error loading AI models:', error);
    }
  };

  const loadRealData = async () => {
    try {
      // Load AI models
      await loadAiModels();

      // Get call stats (lightweight) and campaigns
      const statsResponse = await axios.get(`${API}/calls/stats`);
      const campaignsResponse = await axios.get(`${API}/campaigns`);

      const totalCalls = statsResponse.data.stats?.total || 0;
      const completedCalls = statsResponse.data.stats?.completed || 0;
      const successRate = statsResponse.data.stats?.success_rate || 0;
      const totalCampaigns = campaignsResponse.data.length;

      setStats({
        total_campaigns: totalCampaigns,
        total_calls: totalCalls,
        success_rate: successRate,
        completed_calls: completedCalls
      });

      // Load recent campaigns
      const recentCampaignsData = campaignsResponse.data.slice(0, 5).map(campaign => ({
        id: campaign.id,
        name: campaign.name,
        status: campaign.status || 'active',
        progress: campaign.progress || 0,
        calls_made: campaign.calls_made || 0,
        success_rate: campaign.success_rate || 0
      }));

      setRecentCampaigns(recentCampaignsData);
    } catch (error) {
      console.error('Error fetching real data:', error);
      // Default data on error
      setStats({
        total_campaigns: 0,
        total_calls: 0,
        success_rate: 0,
        completed_calls: 0
      });
      setRecentCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const makeRealCall = async () => {
    // Default values matching ManualCallsPage
    const phone = '+33756834054';
    const script = 'Hello! I am calling you on behalf of VoxiPro AI to present our revolutionary voice automation solution with artificial intelligence. Our platform allows you to automate your prospecting calls with ultra-realistic conversational AI. How are you today?';
    const aiConfig = {
      stt_model: 'whisper_tiny',
      llm_model: 'gpt2_small',
      tts_model: 'piper_tts',
      voice: 'conversational_call',
      system_prompt: 'You are a professional and warm commercial advisor. You express yourself naturally with short to medium sentences, occasional hesitations and reformulations. You adapt your tone and rhythm to the conversation, use active listening markers (ah, I see, okay). You accept not knowing and propose to verify. You politely ask for clarification if you don\'t understand. You use simple and accessible vocabulary. Your goal is to help, advise and build trust. You never mention being an AI.'
    };
    const backgroundAmbiance = 'none';

    if (!phone || !script) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      setLoading(true);
      toast.info('🤖 Initiation d\'un APPEL CONVERSATIONNEL avec IA vers +33756834054...');

      // Use backend URL from environment
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const userId = phone;

      // Call Asterisk backend directly (same as ManualCallsPage)
      const response = await axios.post(`${BACKEND_URL}/api/call/${userId}`, {
        callerId: phone,
        script,
        ai_config: aiConfig,
        background_ambiance: backgroundAmbiance
      });

      // Handle Asterisk backend response (same as ManualCallsPage)
      if (response.data.success || response.status === 200) {
        const callId = response.data.callId || response.data.id || `call_${Date.now()}`;
        toast.success(`✅ Call initiated successfully to ${phone}! ID: ${callId}`);
      } else {
        toast.error(`❌ Call failed: ${response.data.message || 'Unknown error'}`);
      }

    } catch (error) {
      console.error('Error starting manual call:', error);
      toast.error('Error starting call: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl p-8 text-white mb-8 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-3">VoxiPro AI</h1>
            <p className="text-xl text-blue-100 mb-6">Plateforme d'Automatisation Vocale avec IA Native</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-6">
              <div key="whisper-tiny" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.whisper_tiny?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.whisper_tiny?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                OpenAI-Whisper(tiny) (STT) - {
                  aiModels.models?.whisper_tiny?.status === 'running' ? 'Actif' : 
                  aiModels.models?.whisper_tiny?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="faster-whisper" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.faster_whisper?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.faster_whisper?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Faster Whisper Large v3 (STT - FR) - {
                  aiModels.models?.faster_whisper?.status === 'running' ? 'Actif' : 
                  aiModels.models?.faster_whisper?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="qwen2-15b" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.qwen2_15b?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.qwen2_15b?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Qwen2-1.5B (LLM) - {
                  aiModels.models?.qwen2_15b?.status === 'running' ? 'Actif' : 
                  aiModels.models?.qwen2_15b?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="lucie-7b" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.lucie_7b?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.lucie_7b?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Lucie-7B (LLM - FR) - {
                  aiModels.models?.lucie_7b?.status === 'running' ? 'Actif' : 
                  aiModels.models?.lucie_7b?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="kokoro" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.kokoro?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.kokoro?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Kokoro (TTS) - {
                  aiModels.models?.kokoro?.status === 'running' ? 'Actif' : 
                  aiModels.models?.kokoro?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="gtts" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.gtts?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.gtts?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                GTTS (TTS) - {
                  aiModels.models?.gtts?.status === 'running' ? 'Actif' : 
                  aiModels.models?.gtts?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="piper" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.piper?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.piper?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Piper (TTS - FR) - {
                  aiModels.models?.piper?.status === 'running' ? 'Actif' : 
                  aiModels.models?.piper?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="chatterbox" className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${
                  aiModels.models?.chatterbox?.status === 'running' ? 'bg-green-400' : 
                  aiModels.models?.chatterbox?.status === 'available' ? 'bg-yellow-400' : 'bg-red-400'
                }`}></span>
                Chatterbox (TTS) - {
                  aiModels.models?.chatterbox?.status === 'running' ? 'Actif' : 
                  aiModels.models?.chatterbox?.status === 'available' ? 'Non chargé' : 'Indisponible'
                }
              </div>
              <div key="flashcall-sip" className="flex items-center">
                <span className="w-3 h-3 bg-green-400 rounded-full mr-2"></span>
                Flashcall SIP (217.69.5.180) - Connecté
              </div>
            </div>
            <button
              onClick={makeRealCall}
              disabled={false}
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-lg transform hover:scale-105 disabled:opacity-50"
            >
              {loading ? '⏳ APPEL CONVERSATIONNEL EN COURS...' : '🤖 APPEL CONVERSATIONNEL IA +33756834054'}
            </button>
          </div>
          <div className="ml-8">
            <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <img
                src="/favicon_voxipro_détouré.png"
                alt="VoxiPro AI"
                className="w-40 h-40 object-contain"
              />
            </div>
          </div>
        </div>
      </div>


      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div key="stats-campaigns" className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <span className="text-2xl">📢</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Campagnes Total</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total_campaigns || 0}</p>
            </div>
          </div>
        </div>

        <div key="stats-calls" className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <span className="text-2xl">📞</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Appels Total</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total_calls || 0}</p>
            </div>
          </div>
        </div>

        <div key="stats-success" className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Taux de Réussite</p>
              <p className="text-3xl font-bold text-gray-900">{stats.success_rate || 0}%</p>
            </div>
          </div>
        </div>

        <div key="stats-completed" className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
              <span className="text-2xl">🎯</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Appels Terminés</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completed_calls || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Campagnes Récentes</h2>
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appels</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Réussite</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentCampaigns.length > 0 ? (
                recentCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                        campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.calls_made || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.success_rate || 0}%
                    </td>
                  </tr>
                ))
              ) : (
                <tr key="no-campaigns">
                  <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    Aucune campagne récente trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;